import { Component, OnInit, OnDestroy } from '@angular/core';
import { WebsocketService, ConnectionStatus } from 'src/app/services/websocket.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-payment-in-progress',
  templateUrl: './payment-in-progress.component.html',
  styleUrls: ['./payment-in-progress.component.scss']
})
export class PaymentInProgressComponent implements OnInit, OnDestroy {
  private wsMessageListenerId: number | null = null;
  private subscriptions: Subscription[] = [];
  private operationId: string | null = null;
  private pingInterval: any = null;

  constructor(
    private websocketService: WebsocketService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    console.log('PaymentInProgressComponent: Inicializando PaymentInProgressComponent');
    // Obtener el operationId de los query params
    this.operationId = this.activatedRoute.snapshot.queryParams['operationId'];
    
    if (!this.operationId) {
      console.warn('PaymentInProgressComponent: No se encontró operationId en los query params');
      return;
    }

    console.log('PaymentInProgressComponent: Inicializando WebSocket listener con operationId:', this.operationId);
    this.setupWebSocketListener();
    this.activateWebSocketListener();
  }

  /**
   * Configura el listener del WebSocket para escuchar eventos
   */
  private setupWebSocketListener(): void {
    // Escuchar el estado de conexión del WebSocket
    const statusSubscription = this.websocketService.getStatus().subscribe(status => {
      if (status === ConnectionStatus.Connected) {
        console.log('WebSocket conectado en PaymentInProgressComponent');
        // Cuando se conecta, enviar el operationId al WebSocket
        this.sendOperationIdToWebSocket();
        // Iniciar el ping periódico para mantener la conexión viva
        this.initSocketPing();
      } else if (status === ConnectionStatus.Disconnected) {
        console.warn('WebSocket desconectado en PaymentInProgressComponent');
        // Detener el ping cuando se desconecta
        this.stopSocketPing();
      }
    });
    this.subscriptions.push(statusSubscription);
  }

  /**
   * Envía el operationId al WebSocket
   */
  private sendOperationIdToWebSocket(): void {
    if (this.operationId) {
      const message = {
        operationId: this.operationId
      };
      console.log('Enviando operationId al WebSocket:', message);
      this.websocketService.sendMessage(message);
    }
  }

  /**
   * Activa el listener del WebSocket para escuchar eventos
   */
  private activateWebSocketListener(): void {
    if (this.wsMessageListenerId !== null) {
      // Ya hay un listener activo, remover el anterior primero
      this.websocketService.removeMessageListener(this.wsMessageListenerId);
      this.wsMessageListenerId = null;
    }

    console.log('Activando listener del WebSocket para escuchar eventos de pago...');

    // Agregar listener para mensajes del WebSocket
    this.wsMessageListenerId = this.websocketService.addMessageListener((message: any) => {
      console.log('Evento recibido del WebSocket en PaymentInProgressComponent:', message);
      
      // Procesar el mensaje y redirigir a la URL que viene en el mensaje
      this.handleWebSocketEvent(message);
    });

    // Si el WebSocket ya está conectado, enviar el operationId inmediatamente
    const currentStatus = this.websocketService.getStatus();
    const statusSubscription = currentStatus.subscribe((status: ConnectionStatus) => {
      if (status === ConnectionStatus.Connected) {
        this.sendOperationIdToWebSocket();
        // Solo necesitamos enviar una vez cuando se conecta
        statusSubscription.unsubscribe();
      }
    });
    this.subscriptions.push(statusSubscription);
  }

  /**
   * Maneja el evento recibido del WebSocket y redirige a la URL del mensaje
   */
  private handleWebSocketEvent(message: any): void {
    console.log('Procesando evento del WebSocket:', message);
    
    // Buscar la URL en diferentes propiedades posibles del mensaje
    let redirectUrl: string | null = null;
    

    if (message?.url) {
      redirectUrl = message.url;
    }
    
    if (redirectUrl) {
      console.log('Redirigiendo a URL del mensaje:', redirectUrl);
      
      // Verificar si es una URL absoluta (http/https) o relativa
      if (redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')) {
        // URL absoluta, usar window.location
        window.location.href = redirectUrl;
      } else {
        // URL relativa, usar router
        this.router.navigate([redirectUrl]);
      }
    } else {
      console.warn('No se encontró URL en el mensaje del WebSocket:', message);
    }
  }

  /**
   * Inicializa el ping periódico para mantener la conexión WebSocket viva
   */
  private initSocketPing(): void {
    // Limpiar cualquier ping anterior
    this.stopSocketPing();
    
    // Enviar ping cada 10 segundos (similar a payin-backoffice)
    this.pingInterval = setInterval(() => {
      this.sendPing();
    }, 10000);
  }

  /**
   * Detiene el ping periódico
   */
  private stopSocketPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Envía un mensaje ping al WebSocket
   */
  private sendPing(): void {
    const message = {
      topic: 'ping',
      data: {}
    };
    console.log('Enviando ping al WebSocket');
    this.websocketService.sendMessage(message);
  }

  ngOnDestroy(): void {
    // Detener el ping
    this.stopSocketPing();
    
    // Limpiar suscripciones
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    
    // Remover listener del WebSocket si existe
    if (this.wsMessageListenerId !== null) {
      this.websocketService.removeMessageListener(this.wsMessageListenerId);
      this.wsMessageListenerId = null;
    }
  }
}
